import { borgAnalytics as borgAnalyticsClient, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../contexts/appContext';
import { useLanguage } from '../../contexts/languageContext';
import { WidgetEvent } from '../../shared/@types/widgetEvents';
import { AddDifferentPhoneNumber } from '../../shared/components/partials/verification/AddDifferentPhoneNumber';
import { EmailConfirm } from '../../shared/components/partials/verification/EmailConfirm';
import { ErrorPage } from '../../shared/components/partials/verification/ErrorPage';
import { finishVerification } from '../../shared/components/partials/verification/Finish';
import { GetEmail } from '../../shared/components/partials/verification/GetEmail';
import { PhoneNumberConfirm } from '../../shared/components/partials/verification/PhoneNumberConfirm';
import { SelectPhoneNumber } from '../../shared/components/partials/verification/SelectPhoneNumber';
import { accountResource } from '../../shared/resource/account.resource';
import { verificatorResource } from '../../shared/resource/verificator.resource';
import { emitProcessPhase, emitWidgetEvent, onWidgetEvent } from '../../shared/utils/emitData';
import { parseJWT } from '../../shared/utils/jwtUtils';

const noopAnalytics = {
  sendVirtualPageView: () => {},
  sendRegistrationEvent: () => {},
  sendLoginEvent: () => {},
};

export const getAnalytics = (enableAnalytics: boolean) => (enableAnalytics ? borgAnalyticsClient : noopAnalytics);

export enum VerificationPhase {
  GetEmail = 'getEmail',
  EmailConfirm = 'emailConfirm',
  SelectPhoneNumber = 'selectPhoneNumber',
  EnterPhoneNumber = 'enterPhoneNumber',
  PhoneNumberConfirm = 'phoneNumberConfirm',
  Finished = 'finished',
  Banned = 'banned',
  Error = 'error',
}

export enum VerificationPath {
  Default = 'default',
  CustomPhoneNumber = 'customPhoneNumber',
  PhoneConfirmRequired = 'phoneConfirmRequired',
  EmailVerification = 'emailVerification',
  PhoneVerification = 'phoneVerification',
}

// JWT payload structure for init params (normalized to camelCase)
export type InitParamsJWTPayload = {
  clientID?: string;
  sessionID?: string;
  withPassword?: boolean;
  captchaNeeded?: boolean;
  emailVerification?: boolean;
  tmsVerification?: boolean;
  msisdnVerification?: boolean;
  createDUP?: boolean;
  salesMsisdn?: string;
  salesCA?: string;
  ipBanningEnabled?: boolean;
  showLoginButton?: boolean;
  language?: 'hu' | 'en';
  theme?: 'dark' | 'light';
  additionalInfo?: Record<string, unknown>;
};

// Parsed init params with defaults applied
export type InitParams = {
  clientID: string;
  sessionID: string;
  withPassword: boolean;
  captchaNeeded: boolean;
  emailVerification: boolean;
  tmsVerification: boolean;
  msisdnVerification: boolean;
  createDUP: boolean;
  salesMsisdn?: string;
  salesCA?: string;
  ipBanningEnabled: boolean;
  showLoginButton: boolean;
  language: 'hu' | 'en' | undefined;
  theme: 'dark' | 'light' | undefined;
  additionalInfo: Record<string, unknown>;
};

// Map of known key variations to normalized camelCase keys
const keyNormalizationMap: Record<string, string> = {
  clientid: 'clientID',
  sessionid: 'sessionID',
  withpassword: 'withPassword',
  captchaneeded: 'captchaNeeded',
  emailverification: 'emailVerification',
  tmsverification: 'tmsVerification',
  msisdnverification: 'msisdnVerification',
  createdup: 'createDUP',
  salesmsisdn: 'salesMsisdn',
  salesca: 'salesCA',
  ipbanningenabled: 'ipBanningEnabled',
  showloginbutton: 'showLoginButton',
  language: 'language',
  theme: 'theme',
  additionalinfo: 'additionalInfo',
};

const normalizeKeys = <T extends Record<string, unknown>>(obj: Record<string, unknown>): T => {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const lowerKey = key.toLowerCase();
    const normalizedKey = keyNormalizationMap[lowerKey] ?? key;
    result[normalizedKey] = obj[key];
  }
  return result as T;
};

const parseInitParams = (token?: string): InitParams | null => {
  if (!token) return null;

  const rawPayload = parseJWT<Record<string, unknown>>(token);
  if (!rawPayload) return null;

  const payload = normalizeKeys<InitParamsJWTPayload>(rawPayload);
  return {
    clientID: payload.clientID ?? '',
    sessionID: payload.sessionID ?? '',
    withPassword: payload.withPassword ?? false,
    captchaNeeded: payload.captchaNeeded ?? true,
    emailVerification: payload.emailVerification ?? true,
    tmsVerification: payload.tmsVerification ?? true,
    msisdnVerification: payload.msisdnVerification ?? true,
    createDUP: payload.createDUP ?? true,
    salesMsisdn: payload.salesMsisdn ?? '',
    salesCA: payload.salesCA ?? '',
    ipBanningEnabled: payload.ipBanningEnabled ?? true,
    showLoginButton: payload.showLoginButton ?? true,
    language: payload.language || undefined,
    theme: payload.theme || undefined,
    additionalInfo: payload.additionalInfo ?? {},
  };
};

type StepConfig = {
  phase: VerificationPhase;
  skip?: boolean;
  requiredPaths?: VerificationPath[];
};

type StepperConfig = {
  steps: StepConfig[];
  initialStep?: number;
  initialPaths?: VerificationPath[];
};

export type StepperControls = {
  currentStep: number;
  currentPhase: VerificationPhase;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  activePaths: Set<VerificationPath>;
  goToNext: () => void;
  goToPrevious: () => void;
  goToStep: (step: number) => void;
  goToPhase: (phase: VerificationPhase) => void;
  addPath: (path: VerificationPath) => void;
  removePath: (path: VerificationPath) => void;
  setPath: (path: VerificationPath, active: boolean) => void;
  hasPath: (path: VerificationPath) => boolean;
  hasPhase: (phase: VerificationPhase) => boolean;
};

const useStepper = (config: StepperConfig, sessionId?: string): StepperControls => {
  const [currentStep, setCurrentStep] = useState(config.initialStep ?? 0);
  const [activePaths, setActivePaths] = useState<Set<VerificationPath>>(
    () => new Set(config.initialPaths ?? [VerificationPath.Default]),
  );

  // Sync activePaths when initialPaths changes (e.g., when initParams becomes available)
  useEffect(() => {
    if (config.initialPaths) {
      setActivePaths(new Set(config.initialPaths));
    }
  }, [config.initialPaths]);

  const activeSteps = useMemo(() => {
    return config.steps.filter(step => {
      if (step.skip) {
        return false;
      }

      if (step.requiredPaths && step.requiredPaths.length > 0) {
        return step.requiredPaths.some(path => activePaths.has(path));
      }

      return true;
    });
  }, [config.steps, activePaths]);

  const totalSteps = activeSteps.length;

  const currentPhase = useMemo(
    () => activeSteps[currentStep]?.phase ?? VerificationPhase.GetEmail,
    [activeSteps, currentStep],
  );

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const goToNext = useCallback(() => {
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
    }
  }, [isLastStep]);

  const goToPrevious = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps],
  );

  const { isWidgetMode } = useApp();

  const goToPhase = useCallback(
    (phase: VerificationPhase) => {
      if (isWidgetMode && phase === VerificationPhase.Error) {
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: { details: 'Technical error' },
            isSuccess: false,
          },
          { sessionId: sessionId || '', noSign: !sessionId },
        );
        return;
      }
      const stepIndex = activeSteps.findIndex(step => step.phase === phase);
      if (stepIndex !== -1) {
        setCurrentStep(stepIndex);
      }
    },
    [activeSteps, isWidgetMode, sessionId],
  );

  const addPath = useCallback((path: VerificationPath) => {
    setActivePaths(prev => {
      const next = new Set(Array.from(prev));
      next.add(path);
      return next;
    });
  }, []);

  const removePath = useCallback((path: VerificationPath) => {
    setActivePaths(prev => {
      const next = new Set(Array.from(prev));
      next.delete(path);
      return next;
    });
  }, []);

  const setPath = useCallback(
    (path: VerificationPath, active: boolean) => {
      if (active) {
        addPath(path);
      } else {
        removePath(path);
      }
    },
    [addPath, removePath],
  );

  const hasPath = useCallback(
    (path: VerificationPath) => {
      return activePaths.has(path);
    },
    [activePaths],
  );

  const hasPhase = useCallback(
    (phase: VerificationPhase) => {
      return activeSteps.some(step => step.phase === phase);
    },
    [activeSteps],
  );

  return {
    currentStep,
    currentPhase,
    totalSteps,
    isFirstStep,
    isLastStep,
    activePaths,
    goToNext,
    goToPrevious,
    goToStep,
    goToPhase,
    addPath,
    removePath,
    setPath,
    hasPath,
    hasPhase,
  };
};

type VerificationComponentProps = {
  initParamsJWT?: string;
  customerDataJWT?: string | null;
  showLoginOnActive?: boolean;
  initialEmail?: string;
  initialPhoneNumber?: string[];
  showFinishScreen?: boolean;
  skipGetEmail?: boolean;
  skipGetPhoneNumber?: boolean;
  emailAgain?: boolean;
  redirectOnError?: boolean;
  disableBackButton?: boolean;
  enableAnalytics?: boolean;
  selectedCA?: string;
};

const VerificationComponentInner = ({
  initParamsJWT,
  customerDataJWT,
  showLoginOnActive = false,
  initialEmail,
  initialPhoneNumber,
  skipGetEmail = false,
  skipGetPhoneNumber = false,
  emailAgain = false,
  redirectOnError = false,
  disableBackButton = false,
  enableAnalytics = false,
  selectedCA,
}: VerificationComponentProps) => {
  const { isWebView, isDark } = useTheme();
  const { setLanguage } = useLanguage();
  const { isWidgetMode } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isPhoneNumbersLoading, setIsPhoneNumbersLoading] = useState(true);
  const [corrId] = useState(() => crypto.randomUUID());

  const initParams = useMemo(() => {
    return parseInitParams(initParamsJWT);
  }, [initParamsJWT]);
  const [email, setEmail] = useState(initialEmail || '');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sessionId, setSessionId] = useState('');
  const sessionInitialized = useRef(false);
  const previousPhaseRef = useRef<VerificationPhase | null>(null);

  useEffect(() => {
    if (initParams?.language) {
      setLanguage(initParams.language);
    }
  }, [initParams?.language, setLanguage]);

  // Determine effective dark mode: initParams.theme takes priority, then isDark from useTheme
  const effectiveDark = initParams?.theme ? initParams.theme === 'dark' : isDark;
  const themeClass = effectiveDark ? 'ytl--dark' : 'ytl--light';
  const oppositeThemeClass = effectiveDark ? 'ytl--light' : 'ytl--dark';

  useEffect(() => {
    if (isWidgetMode) {
      return;
    }

    document.documentElement.classList.remove(oppositeThemeClass);
    document.documentElement.classList.add(themeClass);
  }, [isWidgetMode, oppositeThemeClass, themeClass]);

  useLayoutEffect(() => {
    if (isWebView) {
      document.documentElement.classList.add('ytl--app');
    }
    return () => {
      document.documentElement.classList.remove('ytl--app');
    };
  }, [isWebView]);

  const skipEmailConfirm = initParams ? !initParams.emailVerification : false;
  const skipPhoneConfirm = initParams ? !initParams.msisdnVerification : false;
  const shouldSkipSelectPhoneNumber = useMemo(() => {
    if (skipGetPhoneNumber || skipPhoneConfirm || !initParams) {
      return false;
    }

    const hasSalesMsisdn = (initParams?.salesMsisdn ?? '').trim().length > 0;
    const hasSalesCA = (initParams?.salesCA ?? '').trim().length > 0;
    const hasInitialPhoneNumber = initialPhoneNumber?.some(phone => phone.trim().length > 0) ?? false;

    return !hasSalesMsisdn && !hasSalesCA && !hasInitialPhoneNumber;
  }, [initParams, initialPhoneNumber, skipGetPhoneNumber, skipPhoneConfirm]);

  // Initialize sessionId when component mounts
  useEffect(() => {
    if (!sessionInitialized.current && initParams) {
      sessionInitialized.current = true;
      setIsLoading(true);
      verificatorResource
        .startVerificationProcess(isWidgetMode ? { token: initParamsJWT } : { initParams }, corrId)
        .then(response => {
          // Check for HTTP error status codes (4xx and 5xx)
          if (response.status >= 400) {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: {
                  process: 'startVerificationProcess',
                  details: `HTTP Error ${response.status}: ${response.statusText}`,
                },
                isSuccess: false,
              },
              { noSign: true },
            );
            return;
          }

          // Check for application-level errors in response data
          if (response.data.status === 'error') {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: {
                  process: 'startVerificationProcess',
                  details: response.data,
                },
                isSuccess: false,
              },
              { noSign: true },
            );
            return;
          }
          if (response.data.sessionId) {
            setSessionId(response.data.sessionId);
          } else {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: {
                  process: 'startVerificationProcess',
                  details: 'No sessionId returned in response',
                },
                isSuccess: false,
              },
              { noSign: true },
            );
          }
        })
        .catch(error => {
          console.error('Failed to initialize session:', error);
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'startVerificationProcess',
                details: error,
              },
              isSuccess: false,
            },
            { noSign: true },
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [initParams, corrId, initParamsJWT, isWidgetMode, sessionId]);

  // Build initial paths based on initParams
  const initialPaths = useMemo(() => {
    const paths: VerificationPath[] = [VerificationPath.Default];
    if (!skipEmailConfirm) {
      paths.push(VerificationPath.EmailVerification);
    }
    if (!skipPhoneConfirm) {
      paths.push(VerificationPath.PhoneVerification);
    }
    if (shouldSkipSelectPhoneNumber) {
      paths.push(VerificationPath.CustomPhoneNumber);
    }
    // When skipping phone number entry steps, ensure PhoneNumberConfirm is required
    if (skipGetPhoneNumber && !skipPhoneConfirm) {
      paths.push(VerificationPath.PhoneConfirmRequired);
    }
    return paths;
  }, [shouldSkipSelectPhoneNumber, skipEmailConfirm, skipPhoneConfirm, skipGetPhoneNumber]);

  const stepperConfig: StepperConfig = useMemo(
    () => ({
      steps: [
        { phase: VerificationPhase.GetEmail, requiredPaths: [VerificationPath.EmailVerification], skip: skipGetEmail },
        { phase: VerificationPhase.EmailConfirm, requiredPaths: [VerificationPath.EmailVerification] },
        {
          phase: VerificationPhase.SelectPhoneNumber,
          requiredPaths: [VerificationPath.PhoneVerification],
          skip: skipGetPhoneNumber || shouldSkipSelectPhoneNumber,
        },
        {
          phase: VerificationPhase.EnterPhoneNumber,
          requiredPaths: [VerificationPath.CustomPhoneNumber],
          skip: skipGetPhoneNumber,
        },
        {
          phase: VerificationPhase.PhoneNumberConfirm,
          requiredPaths: [VerificationPath.PhoneConfirmRequired],
        },
        { phase: VerificationPhase.Finished },
        { phase: VerificationPhase.Banned },
        { phase: VerificationPhase.Error },
      ],
      initialStep: 0,
      initialPaths,
    }),
    [initialPaths, shouldSkipSelectPhoneNumber, skipGetEmail, skipGetPhoneNumber],
  );

  const stepper = useStepper(stepperConfig, sessionId);

  // Reset isLoading when navigating to a new phase
  useEffect(() => {
    setIsLoading(false);
  }, [stepper.currentPhase]);

  // Set isFinished when phase is Finished
  useEffect(() => {
    if (stepper.currentPhase === VerificationPhase.Finished) {
      setIsFinished(true);
    }
  }, [stepper.currentPhase]);

  // Listen for ProcessFinished event and set isFinished
  useEffect(() => {
    const cleanup = onWidgetEvent((data: { eventName: string; isSuccess?: boolean }) => {
      if (data.eventName === WidgetEvent.ProcessFinished || data.eventName === WidgetEvent.TechnicalError) {
        setIsFinished(true);
      }
    });

    return cleanup;
  }, []);

  // Validate email if initialEmail is provided (skip if skipGetEmail, as EmailConfirm handles it)
  const emailValidated = useRef(false);
  useEffect(() => {
    if (!emailValidated.current && initialEmail && sessionId && !skipGetEmail) {
      emailValidated.current = true;
      setIsLoading(true);
      verificatorResource
        .validateEmail({ sessionId: sessionId, email: initialEmail }, corrId)
        .then(response => {
          if (response.data.status === 'success' || response.data.status === 'valid') {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.EmailPrechecked,
                eventValue: { email: initialEmail },
                isSuccess: true,
              },
              { sessionId },
            );
          } else if (response.data.status === 'banned') {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.EmailPrechecked,
                eventValue: { email: initialEmail, reason: response.data.status },
                isSuccess: false,
              },
              { sessionId },
            );
            stepper.goToPhase(VerificationPhase.Banned);
            return;
          } else if (response.data.status === 'invalid') {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.EmailPrechecked,
                eventValue: { email: initialEmail, reason: response.data.status },
                isSuccess: false,
              },
              { sessionId },
            );
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: { details: 'Technical error' },
                isSuccess: false,
              },
              { sessionId: sessionId || '', noSign: !sessionId },
            );
            if (!isWidgetMode) {
              stepper.goToPhase(VerificationPhase.Error);
            }
            return;
          } else if (response.data.status === 'error') {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: {
                  process: 'validateInitialEmail',
                  details: response.data,
                },
                isSuccess: false,
              },
              { sessionId },
            );
            if (!isWidgetMode) {
              stepper.goToPhase(VerificationPhase.Error);
            }
            return;
          } else if (response.data.status === 'used') {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.EmailPrechecked,
                eventValue: { email: initialEmail, reason: response.data.status },
                isSuccess: false,
              },
              { sessionId },
            );
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: { details: 'Technical error' },
                isSuccess: false,
              },
              { sessionId: sessionId || '', noSign: !sessionId },
            );
            if (!isWidgetMode) {
              stepper.goToPhase(VerificationPhase.Error);
            }
            return;
          } else {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: {
                  process: 'validateInitialEmail',
                  details: response.data,
                },
                isSuccess: false,
              },
              { sessionId },
            );
            if (!isWidgetMode) {
              stepper.goToPhase(VerificationPhase.Error);
            }
            return;
          }
        })
        .catch(error => {
          console.error('Failed to validate initial email:', error);
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'validateInitialEmail',
                details: error,
              },
              isSuccess: false,
            },
            { sessionId },
          );
          if (!isWidgetMode) {
            stepper.goToPhase(VerificationPhase.Error);
          }
          return;
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [initialEmail, sessionId, stepper, corrId, skipGetEmail, isWidgetMode]);

  // Listen for TechnicalError events and navigate to error page if enabled (non-widget only)
  const errorHandled = useRef(false);
  useEffect(() => {
    if (!redirectOnError || isWidgetMode) return;

    const cleanup = onWidgetEvent((data: { eventName: string; isSuccess?: boolean }) => {
      if (data.eventName === WidgetEvent.TechnicalError && !errorHandled.current) {
        errorHandled.current = true;
        stepper.goToPhase(VerificationPhase.Error);
      }
    });

    return cleanup;
  }, [redirectOnError, stepper, isWidgetMode]);

  // Phone number list state - combines salesCA lookup with initialPhoneNumber
  type PhoneOption = { id: string; label: string; isDefault: boolean };
  const [phoneNumberList, setPhoneNumberList] = useState<PhoneOption[]>([]);

  useEffect(() => {
    const loadPhoneNumbers = async () => {
      setIsPhoneNumbersLoading(true);
      const phoneOptions: PhoneOption[] = [];
      let startIndex = 0;

      // If salesCA exists, fetch phone numbers from API
      const salesCA = initParams?.salesCA;
      if (salesCA && salesCA.length > 0) {
        try {
          const phoneNumbers =
            !isWidgetMode && customerDataJWT
              ? await accountResource.searchCustomerContactPhoneNumbersByCAByAuthProxy(salesCA, customerDataJWT)
              : await accountResource.searchCustomerContactPhoneNumbersByCAByVerificator(salesCA, initParamsJWT);

          // The function returns '#' on error or the phoneNumberList array on success
          if (phoneNumbers === '#' || !Array.isArray(phoneNumbers)) {
            emitWidgetEvent(
              {
                eventName: WidgetEvent.TechnicalError,
                eventValue: {
                  process: 'searchCustomerContactPhoneNumbersByCA',
                  details: 'Failed to fetch phone numbers',
                },
                isSuccess: false,
              },
              { sessionId },
            );
          } else {
            phoneNumbers.forEach((phone, index) => {
              phoneOptions.push({
                id: `salesCA-phone-${index}`,
                label: phone,
                isDefault: phoneOptions.length === 0,
              });
            });
            startIndex = phoneOptions.length;
          }
        } catch (error) {
          console.error('Failed to fetch phone numbers from salesCA:', error);
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'searchCustomerContactPhoneNumbersByCA',
                details: error,
              },
              isSuccess: false,
            },
            { sessionId },
          );
        }
      }

      // Add initialPhoneNumber entries
      if (initialPhoneNumber && initialPhoneNumber.length > 0) {
        initialPhoneNumber.forEach((phone, index) => {
          // Avoid duplicates
          if (!phoneOptions.some(opt => opt.label === phone)) {
            phoneOptions.push({
              id: `phone-${startIndex + index}`,
              label: phone,
              isDefault: phoneOptions.length === 0,
            });
          }
        });
      }

      setPhoneNumberList(phoneOptions);
      setIsPhoneNumbersLoading(false);
    };

    loadPhoneNumbers().catch(() => {
      setIsPhoneNumbersLoading(false);
    });
  }, [customerDataJWT, initParams, initialPhoneNumber, initParamsJWT, isWidgetMode, sessionId]);

  useEffect(() => {}, [isWebView]);

  // Call finishVerification when the Finished phase is reached
  const finishCalled = useRef(false);
  const bannedHandled = useRef(false);
  useEffect(() => {
    if (stepper.currentPhase === VerificationPhase.Finished && !finishCalled.current) {
      finishCalled.current = true;
      finishVerification({
        email,
        phoneNumber,
        initParams,
        sessionId,
        corrId,
        enableAnalytics,
        isWebView,
      });
    }
    if (stepper.currentPhase === VerificationPhase.Banned && !bannedHandled.current) {
      bannedHandled.current = true;
      emitProcessPhase('Banned');
      emitWidgetEvent(
        {
          eventName: WidgetEvent.ProcessFinished,
          eventValue: {
            banned: true,
          },
          isSuccess: false,
        },
        { sessionId: sessionId || '' },
      );
    }
    // Track previous phase (excluding Finished, Banned, Error)
    if (
      stepper.currentPhase !== VerificationPhase.Finished &&
      stepper.currentPhase !== VerificationPhase.Banned &&
      stepper.currentPhase !== VerificationPhase.Error
    ) {
      previousPhaseRef.current = stepper.currentPhase;
    }
  }, [stepper.currentPhase, email, phoneNumber, initParams, sessionId, corrId, enableAnalytics, isWebView]);

  const renderStep = () => {
    // When in Finished or Banned phase, keep showing the previous step
    const phaseToRender =
      (stepper.currentPhase === VerificationPhase.Finished || stepper.currentPhase === VerificationPhase.Banned) &&
      previousPhaseRef.current
        ? previousPhaseRef.current
        : stepper.currentPhase;

    switch (phaseToRender) {
      case VerificationPhase.GetEmail:
        return (
          <GetEmail
            stepper={stepper}
            initParams={initParams}
            showLoginOnActive={showLoginOnActive}
            email={email}
            setEmail={setEmail}
            sessionId={sessionId}
            emailAgain={emailAgain}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isFinished={isFinished}
            corrId={corrId}
            enableAnalytics={enableAnalytics}
            selectedCA={selectedCA}
          />
        );
      case VerificationPhase.EmailConfirm:
        return (
          <EmailConfirm
            stepper={stepper}
            initParams={initParams}
            email={email}
            sessionId={sessionId}
            setSessionId={setSessionId}
            skipGetEmail={skipGetEmail}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isFinished={isFinished}
            corrId={corrId}
            disableBackButton={disableBackButton}
            enableAnalytics={enableAnalytics}
          />
        );
      case VerificationPhase.SelectPhoneNumber:
        return (
          <SelectPhoneNumber
            stepper={stepper}
            initParams={initParams}
            setPhoneNumber={setPhoneNumber}
            sessionId={sessionId}
            setSessionId={setSessionId}
            phoneOptions={phoneNumberList}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isFinished={isFinished}
            isPhoneNumbersLoading={isPhoneNumbersLoading}
            corrId={corrId}
            enableAnalytics={enableAnalytics}
          />
        );
      case VerificationPhase.EnterPhoneNumber:
        return (
          <AddDifferentPhoneNumber
            stepper={stepper}
            initParams={initParams}
            setPhoneNumber={setPhoneNumber}
            sessionId={sessionId}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isFinished={isFinished}
            corrId={corrId}
            enableAnalytics={enableAnalytics}
          />
        );
      case VerificationPhase.PhoneNumberConfirm:
        return (
          <PhoneNumberConfirm
            stepper={stepper}
            initParams={initParams}
            sessionId={sessionId}
            setSessionId={setSessionId}
            initialPhoneNumber={initialPhoneNumber ? initialPhoneNumber[0] : undefined}
            skipGetPhoneNumber={skipGetPhoneNumber}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isFinished={isFinished}
            corrId={corrId}
            disableBackButton={disableBackButton}
            enableAnalytics={enableAnalytics}
          />
        );
      case VerificationPhase.Finished:
        return null;
      case VerificationPhase.Banned:
        return null;
      case VerificationPhase.Error:
        return (
          <ErrorPage
            stepper={stepper}
            errorCode="UNEXPECTED_ERROR"
          />
        );
      default:
        return null;
    }
  };

  return <div className="verification-step-main">{renderStep()}</div>;
};

export const VerificationComponent = ({
  initParamsJWT: initParamsJWTProp,
  customerDataJWT,
  showLoginOnActive = false,
  initialEmail,
  initialPhoneNumber,
  showFinishScreen = false,
  skipGetEmail = false,
  skipGetPhoneNumber = false,
  emailAgain = false,
  redirectOnError = true,
  disableBackButton = false,
  enableAnalytics = false,
  selectedCA,
}: VerificationComponentProps) => {
  const { initParamsJWT: initParamsJWTContext, isWidgetMode } = useApp();

  // Prefer the direct prop when available; widget mode still falls back to the
  // context copy exposed through AppProvider.
  const initParamsJWT = initParamsJWTProp || initParamsJWTContext;

  // In widget mode, only render when initParamsJWT exists and is valid
  if (isWidgetMode && (!initParamsJWT || initParamsJWT === 'undefined')) {
    emitWidgetEvent(
      {
        eventName: WidgetEvent.TechnicalError,
        eventValue: { details: 'Missing or invalid initParamsJWT' },
        isSuccess: false,
      },
      { sessionId: '' },
    );
    return null;
  }

  // The webcomponent wrapper latches the first valid JWT. Keep widget-mode
  // instances mounted even if a caller refreshes the prop, while preserving
  // JWT-driven remounts for non-widget callers.
  const remountKey = isWidgetMode ? undefined : initParamsJWT;

  return (
    <VerificationComponentInner
      key={remountKey}
      initParamsJWT={initParamsJWT}
      customerDataJWT={customerDataJWT}
      showLoginOnActive={showLoginOnActive}
      initialEmail={initialEmail}
      initialPhoneNumber={initialPhoneNumber}
      showFinishScreen={showFinishScreen}
      skipGetEmail={skipGetEmail}
      skipGetPhoneNumber={skipGetPhoneNumber}
      emailAgain={emailAgain}
      redirectOnError={redirectOnError}
      disableBackButton={disableBackButton}
      enableAnalytics={enableAnalytics}
      selectedCA={selectedCA}
    />
  );
};
